import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarIcon, UserIcon, ArrowLeftIcon, Share2Icon } from 'lucide-react';
import { SEO } from '../components/SEO';
import { blogPosts } from './BlogPage';
export function BlogDetailsPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.id === id);
  if (!post) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center px-4" data-id="element-344">
        <h1 className="text-2xl font-bold mb-4" data-id="element-345">Post not found</h1>
        <button onClick={() => navigate('/blog')} className="text-primary font-bold flex items-center gap-2" data-id="element-346">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-347" /> Back to Blog
        </button>
      </div>;
  }
  return <main data-id="element-348">
      <SEO title={post.title} description={post.excerpt} data-id="element-349" />

      {/* Article Header */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 bg-neutral-light" data-id="element-350">
        <div className="max-w-4xl mx-auto px-4" data-id="element-351">
          <Link to="/blog" className="inline-flex items-center gap-2 text-primary font-bold mb-8 hover:-translate-x-1 transition-transform" data-id="element-352">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-353" /> Back to Blog
          </Link>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-354">
            <span className="bg-accent text-neutral-dark px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block" data-id="element-355">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-neutral-dark mb-8 leading-tight" data-id="element-356">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 pt-8 border-t border-gray-200" data-id="element-357">
              <div className="flex items-center gap-3" data-id="element-358">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-359">
                  <UserIcon className="w-6 h-6 text-primary" data-id="element-360" />
                </div>
                <div data-id="element-361">
                  <p className="text-sm font-bold text-neutral-dark" data-id="element-362">
                    {post.author}
                  </p>
                  <p className="text-xs text-neutral-medium" data-id="element-363">Author</p>
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-364">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center" data-id="element-365">
                  <CalendarIcon className="w-6 h-6 text-accent" data-id="element-366" />
                </div>
                <div data-id="element-367">
                  <p className="text-sm font-bold text-neutral-dark" data-id="element-368">
                    {post.date}
                  </p>
                  <p className="text-xs text-neutral-medium" data-id="element-369">Published</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 px-4 bg-white" data-id="element-370">
        <div className="max-w-4xl mx-auto" data-id="element-371">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="rounded-3xl overflow-hidden mb-16 shadow-2xl" data-id="element-372">
            <img src={post.image} alt={post.title} className="w-full h-auto" data-id="element-373" />
          </motion.div>

          <div className="prose prose-lg prose-neutral max-w-none" data-id="element-374">
            <p className="text-xl text-neutral-dark font-medium leading-relaxed mb-8" data-id="element-375">
              {post.excerpt}
            </p>

            <div className="text-neutral-medium space-y-6 leading-relaxed" data-id="element-376">
              <p data-id="element-377">
                The landscape of home health care is shifting towards more
                personalized, family-centric models. At Ambient Solutions LLC,
                we believe that the best care often comes from those who know
                and love the patient best.
              </p>

              <h2 className="text-2xl font-heading font-bold text-neutral-dark mt-12 mb-4" data-id="element-378">
                Why the Family Caregiver Model Works
              </h2>
              <p data-id="element-379">
                Traditional home health often introduces strangers into the most
                private spaces of a home. While professional, this can sometimes
                lead to anxiety or discomfort for the patient. By empowering
                family members to become certified professionals, we bridge the
                gap between emotional connection and clinical excellence.
              </p>

              <blockquote className="border-l-4 border-accent pl-6 py-2 italic text-neutral-dark font-medium my-8 bg-accent-light/30 rounded-r-xl" data-id="element-380">
                "Empowering families isn't just about convenience; it's about
                dignity, continuity of care, and emotional well-being."
              </blockquote>

              <h2 className="text-2xl font-heading font-bold text-neutral-dark mt-12 mb-4" data-id="element-381">
                Key Considerations
              </h2>
              <ul className="list-disc pl-6 space-y-3" data-id="element-382">
                <li data-id="element-383">
                  <strong data-id="element-384">Clinical Oversight:</strong> Every family caregiver is
                  supervised by a Registered Nurse.
                </li>
                <li data-id="element-385">
                  <strong data-id="element-386">Mandatory Training:</strong> We ensure all caregivers
                  are certified in CPR, First Aid, and basic nursing skills.
                </li>
                <li data-id="element-387">
                  <strong data-id="element-388">Financial Stability:</strong> The model allows family
                  members to receive professional compensation for the care they
                  are already providing.
                </li>
              </ul>

              <p className="mt-12" data-id="element-389">
                As we look to the future of aging in place, the integration of
                family into the professional care team stands out as a beacon of
                sustainable, high-quality home health.
              </p>
            </div>
          </div>

          {/* Share & Footer */}
          <div className="mt-20 pt-10 border-t border-gray-100 flex items-center justify-between" data-id="element-390">
            <button className="flex items-center gap-2 text-neutral-medium hover:text-primary transition-colors font-medium" data-id="element-391">
              <Share2Icon className="w-5 h-5" data-id="element-392" /> Share this article
            </button>
            <Link to="/blog" className="text-primary font-bold" data-id="element-393">
              View all posts
            </Link>
          </div>
        </div>
      </section>
    </main>;
}